import { Button, InputField, THEME } from "@myloc/myloc-gui";
import React, { useEffect, useState } from "react";
import { useTranslate } from "../../language/i18n";
import { setMessage } from "../../reducers/dialog/dialogAction";
import absenceService from "../../services/absence/absenceService";
import { Absence, AbsencePayload } from "../../services/absence/types";
import { getFullDate } from "../../utils/DateUtil";
import MessageBox, { Type as MessageBoxType } from "../shared/MessageBox/MessageBox";
import Modal from "../shared/Modals/Modal";
import styles from "./AbsenceModal.module.scss";

interface Props {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const AbsenceModal = ({ visible, setVisible }: Props) => {
  const translate = useTranslate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;

    absenceService.getAbsence().then(response => {
      if (!response.isOk()) return;

      const data = response.data as Absence;

      setStartDate(getFullDate(data.startDate));
      setEndDate(getFullDate(data.endDate));
    });
  }, [visible]);

  const close = () => {
    if (isLoading) return;

    setVisible(false);
  };

  const saveAbsence = async (payload: AbsencePayload) => {
    setIsLoading(true);

    const response = await absenceService.updateAbsence(payload);

    setIsLoading(false);

    if (response.isOk()) {
      setMessage(translate("CHANGES_SAVED"));
      setVisible(false);
    }
  };

  const oneDateMissing = !!startDate !== !!endDate;
  const endBeforeStart = !!startDate && !!endDate && endDate < startDate;
  const hasError = oneDateMissing || endBeforeStart;

  const handleSave = async () => {
    if (hasError) return;

    await saveAbsence({
      startDate: startDate || null,
      endDate: endDate || null,
    });
  };

  const handleClear = async () => {
    await saveAbsence({ startDate: null, endDate: null });
  };

  const footer = (
    <div className={styles.footer}>
      {(startDate || endDate) && (
        <Button theme={THEME.SECONDARY} customCssClass="" isLoading={isLoading} onClick={handleClear}>
          {translate("ABSENCE_CLEAR")}
        </Button>
      )}
      <Button theme={THEME.SECONDARY} customCssClass="" isLoading={false} disabled={isLoading} onClick={close}>
        {translate("CANCEL")}
      </Button>
      <Button customCssClass="" isLoading={isLoading} disabled={hasError} onClick={handleSave}>
        {translate("SAVE")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={translate("ABSENCE")}
      visible={visible}
      onClose={close}
      showCloseButton={!isLoading}
      footerContent={footer}
    >
      <div className={styles.fields}>
        <InputField
          type="date"
          label={translate("ABSENCE_START")}
          labelAlwaysTop
          id="absenceStartDate"
          name="absenceStartDate"
          value={startDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
          onClear={() => setStartDate("")}
          onFocus={undefined}
          onBlur={undefined}
          customCssClass=""
          customInputCssClass=""
          customFieldIconCss=""
          customInvalidMessage={undefined}
          customWarningMessage={undefined}
          required={false}
          disabled={false}
          readOnly={false}
          showErrorWhenEmpty={false}
          selectInput={undefined}
        />
        <InputField
          type="date"
          label={translate("ABSENCE_END")}
          labelAlwaysTop
          id="absenceEndDate"
          name="absenceEndDate"
          value={endDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
          onClear={() => setEndDate("")}
          min={startDate || undefined}
          onFocus={undefined}
          onBlur={undefined}
          customCssClass=""
          customInputCssClass=""
          customFieldIconCss=""
          customInvalidMessage={undefined}
          customWarningMessage={undefined}
          required={false}
          disabled={false}
          readOnly={false}
          showErrorWhenEmpty={false}
          selectInput={undefined}
        />
        {oneDateMissing && (
          <MessageBox message={translate("ABSENCE_BOTH_DATES_REQUIRED")} type={MessageBoxType.Error} />
        )}
        {endBeforeStart && <MessageBox message={translate("ABSENCE_END_BEFORE_START")} type={MessageBoxType.Error} />}
      </div>
    </Modal>
  );
};

export default AbsenceModal;
